import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getBidderId, getUserIsInGoodStanding } from '@/redux/modules/user';
import { getCatalog, getShouldShowLive } from '@/redux/modules/catalog';
import { getLatestMessageTimestamp, getLiveCatalogData, getLiveItemData } from '@/redux/modules/console';
import { getUiLiveBid, submitLiveBid } from '@/redux/modules/liveBid';
import { openAbsenteeBidModal, openRetractBidModal, openSendSellerMessageModal } from '@/redux/modules/modal';
import { useDispatch, useSelector } from 'react-redux';
import BidConsoleOpener from '@/components/BidConsoleOpener/BidConsoleOpener';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import CardCtaBanner from './CardCtaBanner';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getBidLimitBalance } from '@/redux/modules/bidLimit';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getItem } from '@/redux/modules/item';
import { getItemUrl } from '@/utils/urls';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import { getSeller } from '@/redux/modules/seller';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import ms from 'ms';
import RegisterLink from '@/components/RegisterLink/RegisterLink';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styles from './CardCta.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import WhyCantRetractBidPopover from '@/components/Popovers/WhyCantRetractBidPopover';

type Props = {
    allowBidding?: boolean;
    itemId: number;
    showBanner?: boolean;
    showCta?: boolean;
};

const CardCta = (props: Props) => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const { allowBidding, itemId, showBanner, showCta } = props;
    const bidderId = useSelector(getBidderId, isEqual);
    const { bidderMaxBid, leadingBid, salePrice = 0 } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);
    const { currentAskPrice, leadingBidder: leadingBidderId } = useSelector(
        (state) => getLiveItemData(state, itemId),
        isEqual
    );
    const accountInGoodStanding = useSelector(getUserIsInGoodStanding, isEqual);
    const { catalogId, sellerId, startPrice, title } = useSelector((state) => getItem(state, itemId), isEqual);
    const bidLimitBalance = useSelector((state) => getBidLimitBalance(state, catalogId, itemId), isEqual);
    const { sellerName } = useSelector((state) => getSeller(state, sellerId), isEqual);
    const { catalogStatus, currency = 'usd', title: catalogTitle } = useSelector(
        (state) => getCatalog(state, catalogId),
        isEqual
    );
    const liveCatalogData = useSelector((state) => getLiveCatalogData(state, catalogId), isEqual);
    const liveUpdateTimestamp = useSelector((state) => getLatestMessageTimestamp(state, catalogId), isEqual);

    const leadingBidder = leadingBidderId === bidderId;
    const [, setTime] = React.useState(0);
    React.useEffect(() => {
        const interval = setInterval(() => setTime(Date.now()), 5000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, registered, suspended } = useSelector(
        (state) => getCatalogRegistration(state, catalogId),
        isEqual
    );

    const { submitted } = useSelector(getUiLiveBid, isEqual);
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;

    const showSendSellerMessageModal = React.useCallback(() => {
        const messageDetails = {};
        const subject = `${formatMessage({
            id: 'registration_declined',
        })}: ${catalogTitle}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogTitle, dispatch, formatMessage, sellerId, sellerName]);

    const checkReadyToBid = React.useCallback(() => {
        const { closed, currentItem, networkStatus, status, won } = liveCatalogData;
        const timeSinceLastMessage = Date.now() - liveUpdateTimestamp;

        return (
            itemId === currentItem &&
            itemId > 0 &&
            networkStatus === 'connected' &&
            status === 'live' &&
            !closed &&
            !won &&
            Boolean(currentAskPrice) &&
            catalogId > 0 &&
            timeSinceLastMessage < ms('5m')
        );
    }, [catalogId, currentAskPrice, itemId, liveCatalogData, liveUpdateTimestamp]);

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid();

    const showLiveButton = useSelector((state) => getShouldShowLive(state, catalogId), isEqual);
    const linkAndButton = React.useCallback(() => {
        const itemUrl = getItemUrl(itemId, title);

        const bidAmount = (
            <FormattedCurrency
                className={styles.absenteeBidCurrency}
                noFraction
                sourceCurrency={currency}
                value={bidderMaxBid}
            />
        );
        let link: any = '';
        if (blockedByAuctioneer || suspended || !accountInGoodStanding) {
            link = (
                <Link
                    className={styles['help-button']}
                    to="https://help.liveauctioneers.com/article/146-how-do-i-restore-my-suspended-account"
                >
                    <FormattedMessage id="suspendedBidderBanner.button" />
                </Link>
            );
        } else if (declined) {
            link = (
                <ShouldLogInContainer requireCompleteProfile requireLogin runFunction={showSendSellerMessageModal}>
                    <Link>
                        <FormattedMessage id="contact_auctioneer" />
                    </Link>
                </ShouldLogInContainer>
            );
        } else if (catalogStatus === 'done') {
            link = (
                <Link route to={itemUrl}>
                    <FormattedMessage id="view_similar_items" />
                </Link>
            );
        } else if (showLiveButton) {
            link = (
                <BidConsoleOpener catalogId={catalogId}>
                    <Link className={styles.live}>
                        <FormattedMessage id="enter_live_auction" />
                    </Link>
                </BidConsoleOpener>
            );
        } else {
            link = (
                <Link
                    onClick={() => {
                        dispatch(openAbsenteeBidModal(itemId, 0));
                    }}
                >
                    <FormattedMessage id="increase_bid_of" values={{ bidAmount }} />
                </Link>
            );
        }

        let retractButton;
        if (catalogStatus === 'online') {
            retractButton = (
                <span
                    className={styles.retract}
                    onClick={() => {
                        dispatch(openRetractBidModal(itemId));
                    }}
                >
                    <FormattedMessage id="retract" />
                </span>
            );
        } else if (catalogStatus === 'live') {
            retractButton = (
                <div className={styles.retract}>
                    <WhyCantRetractBidPopover elementId="whyCantRetractBidPopover" />
                </div>
            );
        } else {
            retractButton = <span className={styles.retract} />;
        }
        return (
            <>
                {link}
                {retractButton}
            </>
        );
    }, [
        accountInGoodStanding,
        bidderMaxBid,
        blockedByAuctioneer,
        catalogId,
        catalogStatus,
        currency,
        declined,
        dispatch,
        itemId,
        showLiveButton,
        showSendSellerMessageModal,
        suspended,
        title,
    ]);

    const placeBid = React.useCallback(() => {
        // dont only rely on the "disabled" button to prevent bidders who havent been approved
        if (accountInGoodStanding && (approved || limited) && checkReadyToBid()) {
            dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId));
        } else {
            return false;
        }
    }, [
        accountInGoodStanding,
        approved,
        catalogId,
        checkReadyToBid,
        currency,
        currentAskPrice,
        dispatch,
        itemId,
        limited,
    ]);

    return isLotOpen && allowBidding ? (
        <div className={styles['item-card-control']}>
            {!isStoppedFromBidding && registered && !leadingBidder && (
                <Button disabled={submitted} onClick={placeBid} primary red small>
                    {submitted ? (
                        <Throbber colorWhite />
                    ) : (
                        <FormattedMessage
                            id="bidding_console.console.button.bid"
                            values={{
                                amount: (
                                    <FormattedCurrency
                                        className={styles.bidButtonCurrency}
                                        noFraction
                                        sourceCurrency={currency}
                                        value={currentAskPrice}
                                    />
                                ),
                            }}
                        />
                    )}
                </Button>
            )}
            {leadingBidder && (
                <Button className={styles.winning} primary small>
                    <FormattedMessage id="winning" />
                </Button>
            )}
            {!registered && <RegisterLink catalogId={catalogId} className={styles['register-link']} />}
        </div>
    ) : showCta ? (
        <div className={styles['item-card-control']}>{linkAndButton()}</div>
    ) : showBanner && catalogStatus !== 'done' ? (
        <div className={styles['item-card-control']}>
            <CardCtaBanner catalogId={catalogId} itemId={itemId} />
        </div>
    ) : null;
};

export default React.memo<Props>(CardCta);
